import React, { useEffect, useMemo, useState } from "react";
import {
  North,
  South,
  East,
  West,
  NavigateBefore,
  NavigateNext,
} from "@mui/icons-material";
import { IconButton, Typography, Grid } from "@mui/material";
import RotatableImage from "./RotateableImage";

import {
  formatTafToWindArray,
  formatMetarToWindArray,
  isTrainingMode,
} from "../helpers";
import {
  getTrainingWXFromAllData,
  parseTrainingWX,
} from "../utilities/helperfunctions";

import "../style/runway-data.scss";
import { ReactComponent as RunwaySvg } from "../images/runway.svg";

export const RunwayData = ({
  metar,
  rwyDataArray = [{ rwy: "None" }],
  magneticVariation,
  tafData,
  allData,
  selectedIcao,
}) => {
  const [selectedRunway, setSelectedRunway] = useState(rwyDataArray[0]);
  const [htWind, setHtWind] = useState("");
  const [xWind, setXWind] = useState("");
  const [rawHtWind, setRawHtWind] = useState(0);
  const [rawXWind, setRawXWind] = useState(0);
  const [knotsString, setKnotsString] = useState("");
  const [rotationDegrees, setRotationDegrees] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Get weather data from training mode
  const trainingWeatherData = useMemo(() => {
    const trainingMode = isTrainingMode();
    if (trainingMode && allData && selectedIcao) {
      const trainingWX = getTrainingWXFromAllData(allData, selectedIcao);
      if (trainingWX?.sWX) {
        return parseTrainingWX(trainingWX.sWX);
      }
    }
    return null;
  }, [allData, selectedIcao]);

  const effectiveMetar = useMemo(() => {
    if (trainingWeatherData?.metar) {
      return { raw_text: trainingWeatherData.metar };
    }
    return metar;
  }, [trainingWeatherData, metar]);

  const effectiveTafData = useMemo(() => {
    if (trainingWeatherData?.taf) {
      return trainingWeatherData.taf;
    }
    return tafData;
  }, [trainingWeatherData, tafData]);

  const trainingMode = isTrainingMode();

  const combinedWindData = useMemo(() => {
    const metarWinds = formatMetarToWindArray(
      effectiveMetar?.raw_text,
      trainingMode
    );
    const tafWinds = formatTafToWindArray(effectiveTafData, trainingMode);
    return [...metarWinds, ...tafWinds];
  }, [effectiveMetar?.raw_text, effectiveTafData, trainingMode]);

  let selectedWind = combinedWindData[currentIndex];

  const modalTitle =
    magneticVariation || magneticVariation === 0
      ? `Wind direction compensated for magnetic variation ${Math.abs(
          magneticVariation
        )}${magneticVariation !== 0 ? (magneticVariation < 0 ? "W" : "E") : ""}`
      : "No data for magnetic variation found";

  useEffect(() => {
    setSelectedRunway(rwyDataArray?.[0] || null);
    setCurrentIndex(0);
  }, [effectiveMetar, rwyDataArray]);

  useEffect(() => {
    handleRwyCalculation(selectedRunway);
  }, [
    selectedRunway,
    currentIndex,
    effectiveMetar,
    magneticVariation,
    effectiveTafData,
  ]);

  const handleRwyCalculation = (rwyData) => {
    const magneticVariationValue = Number(magneticVariation) || 0;

    if (!selectedWind || !selectedWind.windDegrees || !selectedWind.windSpeed) {
      setHtWind("");
      setXWind("");
      setKnotsString("- kts");
      return;
    }

    if (
      selectedWind.windDegrees === "VRB" ||
      isNaN(Number(selectedWind.windDegrees))
    ) {
      // No directional calculation possible
      const windSpeed = selectedWind.windSpeed;
      const gust = selectedWind.windGust ? `G${selectedWind.windGust}` : "";
      setHtWind("");
      setXWind("");
      setKnotsString(`VRB/${windSpeed}${gust} kts`);
      setRotationDegrees(null);
      return;
    }

    const climbTrack = Number(rwyData?.rwyHeading);

    // Show wind info even without runway data

    const windDirection = Math.round(
      (Number(selectedWind.windDegrees) - magneticVariationValue + 360) % 360
    );

    const windSpeed = Number(selectedWind.windSpeed);
    const windGust = selectedWind.windGust
      ? Number(selectedWind.windGust)
      : null;
    const gustPart = windGust ? `G${windGust}` : "";

    if (!climbTrack || rwyData?.tora === null) {
      // No runway data - show wind but can't calculate cross wind
      setHtWind("");
      setXWind("");
      setKnotsString(
        `${String(windDirection).padStart(3, "0")}/${windSpeed}${gustPart} kts`
      );
      setRotationDegrees(null);
      return;
    }

    const angle = (Math.PI * (climbTrack - windDirection)) / 180;
    const degree = windDirection - climbTrack;

    const baseHtWind = Math.round(Math.cos(angle) * windSpeed);
    const baseXWind = Math.round(Math.sin(angle) * windSpeed);

    setRawHtWind(baseHtWind);
    setRawXWind(baseXWind);

    let htResult = "";
    let xResult = "";

    // Gust calculation
    if (windGust) {
      const gustHtWind = Math.round(Math.cos(angle) * windGust);
      const gustXWind = Math.round(Math.sin(angle) * windGust);

      htResult = `${Math.abs(baseHtWind)}G${Math.abs(gustHtWind)} kts`;
      xResult =
        baseXWind === 0 && gustXWind === 0
          ? `0G0 kts`
          : `${Math.abs(baseXWind)}G${Math.abs(gustXWind)} kts`;
    } else {
      htResult = `${Math.abs(baseHtWind)} kts`;
      xResult = `${Math.abs(baseXWind)} kts`;
    }

    setKnotsString(
      `${String(windDirection).padStart(3, "0")}/${windSpeed}${gustPart} kts`
    );
    setRotationDegrees(degree);
    setHtWind(baseHtWind === 0 && !windGust ? "0 kts" : htResult);
    setXWind(baseXWind === 0 && !windGust ? "0 kts" : xResult);
  };

  const handleSelectRunway = (rwyData) => {
    if (rwyData && rwyData.tora != null) {
      setSelectedRunway(rwyData);
    }
  };

  const handleNextWind = () => {
    if (!combinedWindData.length) return;
    const nextIndex = (currentIndex + 1) % combinedWindData.length;
    setCurrentIndex(nextIndex);
  };

  const handlePreviousWind = () => {
    if (!combinedWindData.length) return;
    const prevIndex =
      (currentIndex - 1 + combinedWindData.length) % combinedWindData.length;
    setCurrentIndex(prevIndex);
  };

  return (
    <div className="runway-data-container">
      <div className="runway-option-container">
        {rwyDataArray.map((data, index) => (
          <div
            key={index}
            style={{
              padding: "4px 10px",
              cursor: "pointer",
              border:
                data.rwy === selectedRunway?.rwy
                  ? "1px solid green"
                  : "1px solid black",
            }}
            onClick={() => handleSelectRunway(data)}
          >
            <p
              style={{
                color: data.rwy === selectedRunway?.rwy ? "green" : "black",
              }}
            >
              {data.rwy}
            </p>
          </div>
        ))}
      </div>
      <Grid container alignItems="center" justifyContent="space-between">
        <Grid item>
          <IconButton aria-label="Previous" onClick={handlePreviousWind}>
            <NavigateBefore style={{ fontSize: 30 }} />
          </IconButton>
        </Grid>

        <Grid item>
          <Typography
            variant="body1"
            align="center"
            style={{ fontWeight: "bold" }}
          >
            {selectedWind?.title}
          </Typography>
        </Grid>

        <Grid item>
          <IconButton aria-label="Next" onClick={handleNextWind}>
            <NavigateNext style={{ fontSize: 30 }} />
          </IconButton>
        </Grid>
      </Grid>
      <div className="runway-data-body">
        <div className="runway-data-container">
          <div className="wind-data-container">
            <p style={{ fontWeight: "bold" }}>TORA</p>
            <p>
              {selectedRunway?.tora != null
                ? `${selectedRunway?.tora}m`
                : "N/A"}
            </p>
          </div>
          <div className="wind-data-container">
            <p style={{ fontWeight: "bold" }}>LDA</p>
            <p>
              {selectedRunway?.lda != null ? `${selectedRunway?.lda}m` : "N/A"}
            </p>
          </div>
          <div className="wind-data-container">
            <p style={{ fontWeight: "bold" }}>Width</p>
            <p>
              {selectedRunway?.rwyWidth != null
                ? `${selectedRunway?.rwyWidth}m`
                : "N/A"}
            </p>
          </div>
        </div>
        <div className="runway-data-container">
          <p style={{ fontWeight: "bold", textAlign: "center" }}>
            {knotsString}
          </p>
          <div style={{ position: "relative" }}>
            <RunwaySvg width={115} height={264} />
            <p className="runway-text">{selectedRunway?.rwy}</p>
          </div>
        </div>
        <div className="runway-data-container">
          <div className="wind-data-container" style={{ overflow: "hidden" }}>
            <RotatableImage rotationDegrees={rotationDegrees} />
          </div>
          <div className="wind-data-container">
            {!selectedRunway?.rwyHeading || !selectedWind?.windDegrees ? (
              <>
                <p style={{ fontWeight: "bold" }}>H‑wind</p>
                <p>N/A</p>
              </>
            ) : (
              <>
                {rawHtWind === 0 ? (
                  <>
                    <p style={{ fontWeight: "bold" }}>No wind</p>
                    <p>0 kts</p>
                  </>
                ) : rawHtWind > 0 ? (
                  <>
                    <South />
                    <p style={{ fontWeight: "bold" }}>H‑wind</p>
                    <p>{htWind}</p>
                  </>
                ) : (
                  <>
                    <North />
                    <p style={{ fontWeight: "bold" }}>T‑wind</p>
                    <p>{htWind}</p>
                  </>
                )}
              </>
            )}
          </div>
          <div className="wind-data-container">
            {!selectedRunway?.rwyHeading || !selectedWind?.windDegrees ? (
              <>
                <p style={{ fontWeight: "bold" }}>X‑wind</p>
                <p>N/A</p>
              </>
            ) : (
              <>
                {rawXWind === 0 ? (
                  <>
                    <p style={{ fontWeight: "bold" }}>No wind</p>
                    <p>0 kts</p>
                  </>
                ) : rawXWind > 0 ? (
                  <>
                    <East />
                    <p style={{ fontWeight: "bold" }}>Left</p>
                    <p>{xWind}</p>
                  </>
                ) : (
                  <>
                    <West />
                    <p style={{ fontWeight: "bold" }}>Right</p>
                    <p>{xWind}</p>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <Grid container>
        <Typography variant="caption">{modalTitle}</Typography>
      </Grid>
    </div>
  );
};
